import { useState, useMemo, useEffect } from 'react';
import { X, Printer, DollarSign, ShieldCheck, AlertCircle, UserCheck } from 'lucide-react';
import { posOrders, posRestaurant, posSettings } from '../../api/posAPI';
import usePosStore from '../../stores/posStore';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmt(centavos) {
  return `₱${((centavos || 0) / 100).toFixed(2)}`;
}

const TRANSACTION_TYPES = [
  { value: 'cash',   label: 'Cash' },
  { value: 'card',   label: 'Card' },
  { value: 'gcash',  label: 'GCash' },
  { value: 'maya',   label: 'Maya' },
  { value: 'charge', label: 'Charge' },
  { value: 'free',   label: 'Free' },
];

// ─── SC/PWD discount computation (centavos, VAT-inclusive prices) ─────────────

function computeSCPWD({ items, selectedDiscountItems, seniorPax, pax }) {
  if (!seniorPax || !pax || seniorPax <= 0) return { promoDiscount: 0 };
  let discountAmount = 0;
  let vatAdjustment = 0;
  for (const item of items) {
    if (!selectedDiscountItems.includes(item._id.toString())) continue;
    const withoutVAT = Math.round(item.subtotal / 1.12);
    discountAmount += Math.round(withoutVAT * 0.20);
    vatAdjustment  += Math.round(withoutVAT * 0.12);
  }
  const ratio = Math.min(1, parseInt(seniorPax) / parseInt(pax));
  const d = Math.round(discountAmount * ratio);
  const v = Math.round(vatAdjustment  * ratio);
  return { discountAmount: d, vatAdjustment: v, promoDiscount: d + v };
}

function discountedSubtotal(items, selectedDiscountItems) {
  return items
    .filter(i => selectedDiscountItems.includes(i._id.toString()))
    .reduce((s, i) => s + i.subtotal, 0);
}

// ─── Cashier PIN panel ────────────────────────────────────────────────────────

function CashierPinPanel({ onVerified }) {
  const { posToken, posStaff } = usePosStore();
  const [pin, setPin]       = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError]   = useState(null);

  const handleVerify = async () => {
    if (!pin) return;
    setError(null);
    setLoading(true);
    try {
      const res = await posRestaurant.verifyPin(posToken, { staffId: posStaff._id, pin });
      if (!res.data.verified) throw new Error('Invalid PIN');
      onVerified();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Verification failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col items-center justify-center h-64 gap-4 px-8">
      <ShieldCheck className="h-10 w-10 text-blue-600" />
      <p className="text-sm text-gray-600 text-center">Enter your PIN to unlock billing</p>
      {error && (
        <div className="w-full p-2 bg-red-50 border border-red-200 rounded text-sm text-red-700 text-center">
          {error}
        </div>
      )}
      <input
        type="password"
        placeholder="Cashier PIN"
        maxLength={8}
        value={pin}
        onChange={(e) => setPin(e.target.value)}
        onKeyDown={(e) => e.key === 'Enter' && handleVerify()}
        className="w-full max-w-xs border border-gray-300 rounded-lg px-3 py-2 text-sm text-center tracking-widest"
        autoFocus
      />
      <button
        onClick={handleVerify}
        disabled={!pin || loading}
        className="w-full max-w-xs py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-semibold disabled:opacity-50"
      >
        {loading ? 'Verifying…' : 'Unlock'}
      </button>
    </div>
  );
}

// ─── Manager approval panel ───────────────────────────────────────────────────

function ManagerApprovalPanel({ onApproved, onCancel }) {
  const { posToken } = usePosStore();
  const [managers, setManagers]       = useState([]);
  const [managerId, setManagerId]     = useState('');
  const [pin, setPin]                 = useState('');
  const [loading, setLoading]         = useState(false);
  const [error, setError]             = useState(null);

  useEffect(() => {
    posRestaurant.staff(posToken, { role: 'manager,supervisor' })
      .then(res => setManagers(res.data.staff || []))
      .catch(() => {});
  }, []);

  const handleApprove = async () => {
    if (!managerId || !pin) return;
    setError(null);
    setLoading(true);
    try {
      const res = await posRestaurant.verifyPin(posToken, { staffId: managerId, pin });
      if (!res.data.verified) throw new Error('Invalid PIN');
      if (!['manager', 'supervisor'].includes(res.data.role)) {
        throw new Error('Staff is not a manager or supervisor');
      }
      onApproved({ managerId: res.data.staffId, managerName: res.data.name });
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Verification failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="border border-amber-200 rounded-xl p-3 bg-amber-50 space-y-2.5">
      <div className="flex items-center gap-2">
        <UserCheck className="h-4 w-4 text-amber-700 flex-shrink-0" />
        <span className="text-xs font-semibold text-amber-800">Manager Approval Required</span>
        <button onClick={onCancel} className="ml-auto text-gray-400 hover:text-gray-600">
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
      {error && <p className="text-xs text-red-600 bg-red-50 rounded p-1.5">{error}</p>}
      <select
        className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm bg-white"
        value={managerId}
        onChange={e => { setManagerId(e.target.value); setPin(''); setError(null); }}
      >
        <option value="">Select manager…</option>
        {managers.map(m => (
          <option key={m._id} value={m._id}>{m.name} ({m.role})</option>
        ))}
      </select>
      <input
        type="password"
        placeholder="Manager PIN"
        maxLength={8}
        value={pin}
        onChange={e => setPin(e.target.value)}
        onKeyDown={e => e.key === 'Enter' && handleApprove()}
        className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm text-center tracking-widest disabled:bg-gray-100"
        disabled={!managerId}
        autoFocus={!!managerId}
      />
      <button
        onClick={handleApprove}
        disabled={!managerId || !pin || loading}
        className="w-full py-1.5 bg-amber-600 hover:bg-amber-700 text-white rounded-lg text-sm font-semibold disabled:opacity-50"
      >
        {loading ? 'Verifying…' : 'Approve'}
      </button>
    </div>
  );
}

// ─── Main BillingModal ────────────────────────────────────────────────────────

export default function BillingModal({ isOpen, order, onClose, onPaid }) {
  const { posToken } = usePosStore();

  const [cashierVerified, setCashierVerified] = useState(false);

  // Discount type: null | 'SC_PWD' | 'MANAGER_DISCOUNT' | 'EMPLOYEE_DISCOUNT'
  const [discountType, setDiscountType] = useState(null);

  // SC/PWD fields
  const [seniorPax,    setSeniorPax]    = useState(0);
  const [pax,          setPax]          = useState(order?.pax || 1);
  // One entry per SC/PWD guest — grows/shrinks with seniorPax
  const [scPwdPersons, setScPwdPersons] = useState([{ name: '', idNo: '', subType: null }]);

  // Manager / employee discount fields
  const [managerApproved,       setManagerApproved]       = useState(null); // { managerId, managerName }
  const [showApprovalPanel,     setShowApprovalPanel]     = useState(false);
  const [managerDiscountAmount, setManagerDiscountAmount] = useState(''); // ₱ string
  const [employeeDiscountPct,   setEmployeeDiscountPct]   = useState(20); // from server

  // Shared: which items receive the discount
  const [selectedDiscountItems, setSelectedDiscountItems] = useState([]);

  // Payment state
  const [transactionType, setTransactionType] = useState('cash');
  const [amountTendered,  setAmountTendered]  = useState('');
  const [remarks,         setRemarks]         = useState('');

  // UI state
  const [printing, setPrinting] = useState(false);
  const [paying,   setPaying]   = useState(false);
  const [error,    setError]    = useState(null);

  // Fetch employee discount % and pre-fill SC/PWD from pendingDiscount on PIN verify
  useEffect(() => {
    if (!cashierVerified || !order) return;

    posSettings.getPosSettings(posToken)
      .then(res => {
        const pct = res.data.settings?.employeeDiscountPercent;
        if (pct != null) setEmployeeDiscountPct(pct);
      })
      .catch(() => {});

    posOrders.get(posToken, order._id)
      .then(res => {
        const pd = res.data.order?.pendingDiscount;
        if (pd?.type === 'SC_PWD' && pd?.seniorPax > 0) {
          setDiscountType('SC_PWD');
          setSeniorPax(pd.seniorPax);
          setPax(pd.totalPax || order.pax || 1);
          setSelectedDiscountItems((pd.discountedItemIds || []).map(String));
          // Restore persons — new format preferred, fall back to legacy single fields
          if (pd.scPwdPersons?.length) {
            setScPwdPersons(pd.scPwdPersons.map(p => ({
              name: p.name || '', idNo: p.idNo || '', subType: p.subType || null,
            })));
          } else {
            setScPwdPersons([{ name: pd.seniorName || '', idNo: pd.seniorId || '', subType: null }]);
          }
        }
      })
      .catch(() => {});
  }, [cashierVerified]);

  const activeItems = useMemo(() => order?.items?.filter(i => !i.voidedAt) || [], [order]);
  // subtotal = sum of VAT-inclusive item prices (always correct).
  const grossTotal = order?.subtotal || 0;

  // ── Discount computation (display only; server recomputes authoritatively) ──
  const discountResult = useMemo(() => {
    if (!discountType || selectedDiscountItems.length === 0) return { promoDiscount: 0 };

    if (discountType === 'SC_PWD') {
      return computeSCPWD({ items: activeItems, selectedDiscountItems, seniorPax, pax });
    }

    const sub = discountedSubtotal(activeItems, selectedDiscountItems);

    if (discountType === 'MANAGER_DISCOUNT' && managerApproved) {
      const fixed = Math.round(parseFloat(managerDiscountAmount || 0) * 100);
      return { promoDiscount: Math.min(fixed, sub) };
    }

    if (discountType === 'EMPLOYEE_DISCOUNT' && managerApproved) {
      return { promoDiscount: Math.round(sub * employeeDiscountPct / 100) };
    }

    return { promoDiscount: 0 };
  }, [discountType, seniorPax, pax, selectedDiscountItems, activeItems,
      managerApproved, managerDiscountAmount, employeeDiscountPct]);

  const promoDiscount = discountResult.promoDiscount;
  const finalAmount   = grossTotal - promoDiscount;
  const tenderedCents = Math.round(parseFloat(amountTendered || 0) * 100);
  const change        = Math.max(0, tenderedCents - finalAmount);

  const toggleDiscountItem = (itemId) => {
    setSelectedDiscountItems(prev =>
      prev.includes(itemId) ? prev.filter(id => id !== itemId) : [...prev, itemId]
    );
  };

  const selectAllItems = () =>
    setSelectedDiscountItems(activeItems.map(i => i._id.toString()));

  // ── Discount type switching ───────────────────────────────────────────────
  // Resize scPwdPersons when seniorPax changes
  useEffect(() => {
    const count = Math.max(1, parseInt(seniorPax) || 1);
    setScPwdPersons(prev => {
      if (prev.length === count) return prev;
      if (prev.length < count) {
        return [...prev, ...Array.from({ length: count - prev.length }, () => ({ name: '', idNo: '', subType: null }))];
      }
      return prev.slice(0, count);
    });
  }, [seniorPax]);

  const switchDiscount = (type) => {
    setDiscountType(type);
    setSelectedDiscountItems([]);
    setManagerApproved(null);
    setShowApprovalPanel(false);
    setManagerDiscountAmount('');
    setSeniorPax(0);
    setScPwdPersons([{ name: '', idNo: '', subType: null }]);
    setPax(order?.pax || 1);
    setError(null);
  };

  const handleSelectDiscount = (type) => {
    if (discountType === type) {
      switchDiscount(null); // toggle off
      return;
    }
    switchDiscount(type);
    if (type === 'MANAGER_DISCOUNT' || type === 'EMPLOYEE_DISCOUNT') {
      setShowApprovalPanel(true);
    }
  };

  const handleManagerApproved = (approval) => {
    setManagerApproved(approval);
    setShowApprovalPanel(false);
  };

  // ── Build discountData for API ────────────────────────────────────────────
  const buildDiscountData = () => {
    if (!discountType || promoDiscount <= 0) return null;

    if (discountType === 'SC_PWD') {
      const firstPerson = scPwdPersons[0] || {};
      return {
        type: 'SC_PWD',
        subType:                firstPerson.subType || null,
        seniorPax:              parseInt(seniorPax) || 0,
        totalPax:               parseInt(pax) || 1,
        seniorName:             firstPerson.name   || '',
        seniorId:               firstPerson.idNo   || '',
        scPwdPersons,
        discountedItemIds:      selectedDiscountItems,
        discountAmount:         discountResult.discountAmount || 0,
        vatAdjustment:          discountResult.vatAdjustment  || 0,
        totalDiscountableAmount: discountResult.discountAmount || 0,
        promoDiscount,
      };
    }

    if (discountType === 'MANAGER_DISCOUNT') {
      return {
        type: 'MANAGER_DISCOUNT',
        managerId:         managerApproved?.managerId,
        managerName:       managerApproved?.managerName,
        fixedAmount:       Math.round(parseFloat(managerDiscountAmount || 0) * 100),
        discountedItemIds: selectedDiscountItems,
        promoDiscount,
      };
    }

    if (discountType === 'EMPLOYEE_DISCOUNT') {
      return {
        type: 'EMPLOYEE_DISCOUNT',
        managerId:         managerApproved?.managerId,
        managerName:       managerApproved?.managerName,
        discountPercent:   employeeDiscountPct,
        discountedItemIds: selectedDiscountItems,
        promoDiscount,
      };
    }

    return null;
  };

  // ── Actions ───────────────────────────────────────────────────────────────
  const handlePrintBilling = async () => {
    setPrinting(true);
    setError(null);
    try {
      await posOrders.printBilling(posToken, order._id, {
        discountData: buildDiscountData(),
      });
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Print failed');
    } finally {
      setPrinting(false);
    }
  };

  const canPay = transactionType === 'cash' ? tenderedCents >= finalAmount : true;

  const handleProcessPayment = async () => {
    setError(null);
    if (transactionType === 'cash' && tenderedCents < finalAmount) {
      setError('Tendered amount is less than total due');
      return;
    }
    setPaying(true);
    try {
      const res = await posOrders.payment(posToken, order._id, {
        method:        transactionType,
        amountTendered: transactionType === 'cash' ? tenderedCents : finalAmount,
        discountData:  buildDiscountData(),
        remarks,
      });
      onPaid(res.data.order);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Payment failed');
    } finally {
      setPaying(false);
    }
  };

  if (!isOpen || !order) return null;

  const showItemCheckboxes = discountType !== null && selectedDiscountItems !== undefined;
  const discSub = discountedSubtotal(activeItems, selectedDiscountItems);

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 flex-shrink-0">
          <div>
            <h2 className="font-bold text-gray-900 text-lg">Billing — {order.tableName || order.orderNo}</h2>
            <p className="text-xs text-gray-500 mt-0.5">Order #{order.orderNo} · Waiter: {order.waiterName}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="h-5 w-5" />
          </button>
        </div>

        {!cashierVerified ? (
          <CashierPinPanel onVerified={() => setCashierVerified(true)} />
        ) : (
          <div className="flex-1 overflow-hidden flex divide-x divide-gray-200">
            {/* ── LEFT: Order Details ──────────────────────────────────────── */}
            <div className="flex-1 flex flex-col overflow-hidden">
              <div className="flex-1 overflow-y-auto px-5 py-4">
                {/* Column headers */}
                <div className="flex text-xs font-semibold text-gray-500 mb-2 pb-1 border-b border-gray-200">
                  <span className="w-8 text-right">Qty</span>
                  <span className="flex-1 ml-3">Product</span>
                  <span className="w-20 text-right">Amount</span>
                  {showItemCheckboxes && discountType && (
                    <button
                      className="w-8 text-center ml-2 text-blue-600 hover:text-blue-800 text-[10px] font-medium"
                      onClick={selectAllItems}
                      title="Select all"
                    >
                      All
                    </button>
                  )}
                </div>

                {/* Items */}
                {activeItems.map((item) => (
                  <div key={item._id} className="flex items-center py-1.5 border-b border-gray-100 last:border-0">
                    <span className="w-8 text-right text-sm text-gray-500">{item.quantity}</span>
                    <span className="flex-1 ml-3 text-sm text-gray-900">{item.name}</span>
                    <span className="w-20 text-right text-sm font-medium text-gray-900">{fmt(item.subtotal)}</span>
                    {showItemCheckboxes && discountType && (
                      <input
                        type="checkbox"
                        className="w-4 h-4 ml-2 accent-blue-600"
                        checked={selectedDiscountItems.includes(item._id.toString())}
                        onChange={() => toggleDiscountItem(item._id.toString())}
                      />
                    )}
                  </div>
                ))}
              </div>

              {/* Totals */}
              <div className="px-5 py-3 border-t border-gray-200 space-y-1 flex-shrink-0 bg-gray-50">
                <div className="flex justify-between text-sm text-gray-600">
                  <span>Total Amount</span>
                  <span>{fmt(grossTotal)}</span>
                </div>
                {promoDiscount > 0 && (
                  <div className="flex justify-between text-sm text-gray-600">
                    <span>Discountable Subtotal</span>
                    <span>{fmt(discSub)}</span>
                  </div>
                )}
                <div className="flex justify-between text-sm text-gray-600">
                  <span>Discount</span>
                  <span className={promoDiscount > 0 ? 'text-green-700 font-medium' : ''}>
                    -{fmt(promoDiscount)}
                  </span>
                </div>
                <div className="flex justify-between text-sm font-bold text-gray-900 pt-1 border-t border-gray-300">
                  <span>Total Amount Due</span>
                  <span>{fmt(finalAmount)}</span>
                </div>
                <div className="flex justify-between text-sm text-gray-700">
                  <span>Change</span>
                  <span>{fmt(change)}</span>
                </div>
              </div>

              {/* Action buttons */}
              <div className="px-5 pb-4 pt-2 flex flex-col gap-2 flex-shrink-0">
                {error && (
                  <div className="flex items-start gap-2 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-700">
                    <AlertCircle className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
                    <span>{error}</span>
                  </div>
                )}
                <button
                  onClick={handlePrintBilling}
                  disabled={printing}
                  className="flex items-center justify-center gap-2 py-2.5 border border-gray-300 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  <Printer className="h-4 w-4" />
                  {printing ? 'Printing…' : 'Print Billing Statement'}
                </button>
                <button
                  onClick={handleProcessPayment}
                  disabled={paying || !canPay}
                  className="flex items-center justify-center gap-2 py-2.5 bg-green-600 hover:bg-green-700 text-white rounded-xl text-sm font-semibold disabled:opacity-50"
                >
                  <DollarSign className="h-4 w-4" />
                  {paying ? 'Processing…' : `Process Payment ${fmt(finalAmount)}`}
                </button>
                <button onClick={onClose} className="py-2 text-sm text-gray-500 hover:text-gray-700">
                  Close
                </button>
              </div>
            </div>

            {/* ── RIGHT: Payment Details ───────────────────────────────────── */}
            <div className="w-72 flex-shrink-0 flex flex-col overflow-y-auto px-5 py-4 gap-4">
              {/* Transaction type */}
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-2">Transaction Type</label>
                <div className="grid grid-cols-3 gap-1.5">
                  {TRANSACTION_TYPES.map(({ value, label }) => (
                    <button
                      key={value}
                      type="button"
                      onClick={() => setTransactionType(value)}
                      className={`py-1.5 rounded-lg border text-xs font-medium transition-colors ${
                        transactionType === value
                          ? 'border-blue-600 bg-blue-50 text-blue-700'
                          : 'border-gray-300 text-gray-700 hover:border-gray-400'
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              {/* ── Discount Section ─────────────────────────────────────── */}
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-2">Discount</label>

                {/* Discount type selector */}
                <div className="flex flex-col gap-1.5 mb-3">
                  {[
                    { type: 'SC_PWD',           label: 'Senior Citizen / PWD' },
                    { type: 'EMPLOYEE_DISCOUNT', label: `Employee (${employeeDiscountPct}% off)` },
                    { type: 'MANAGER_DISCOUNT',  label: 'Manager Discount' },
                  ].map(({ type, label }) => (
                    <button
                      key={type}
                      type="button"
                      onClick={() => handleSelectDiscount(type)}
                      className={`w-full text-left px-3 py-2 rounded-lg border text-xs font-medium transition-colors ${
                        discountType === type
                          ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                          : 'border-gray-300 text-gray-700 hover:border-gray-400'
                      }`}
                    >
                      {label}
                      {discountType === type && managerApproved && type !== 'SC_PWD' && (
                        <span className="ml-2 text-green-700">✓ {managerApproved.managerName}</span>
                      )}
                    </button>
                  ))}
                </div>

                {/* Manager approval panel */}
                {showApprovalPanel && (
                  <ManagerApprovalPanel
                    onApproved={handleManagerApproved}
                    onCancel={() => { setShowApprovalPanel(false); switchDiscount(null); }}
                  />
                )}

                {/* SC/PWD fields */}
                {discountType === 'SC_PWD' && (
                  <div className="space-y-2 mt-2">
                    {/* Count + Total Pax */}
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1">No. of SC/PWD</label>
                        <input type="number" min="1"
                          className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm"
                          value={seniorPax}
                          onChange={(e) => setSeniorPax(e.target.value)}
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1">Total Pax</label>
                        <input type="number" min="1"
                          className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm"
                          value={pax}
                          onChange={(e) => setPax(e.target.value)}
                        />
                      </div>
                    </div>

                    {/* Per-person rows */}
                    <div className="space-y-2">
                      {scPwdPersons.map((person, idx) => (
                        <div key={idx} className="border border-gray-200 rounded-lg p-2 space-y-1.5 bg-gray-50">
                          <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">
                            Person {idx + 1}
                          </p>
                          {/* SC / PWD type toggle */}
                          <div className="flex gap-1.5">
                            {[{ value: 'SC', label: 'SC' }, { value: 'PWD', label: 'PWD' }].map(({ value, label }) => (
                              <button
                                key={value}
                                type="button"
                                onClick={() => setScPwdPersons(prev => prev.map((p, i) =>
                                  i === idx ? { ...p, subType: p.subType === value ? null : value } : p
                                ))}
                                className={`flex-1 py-1 rounded border text-xs font-medium transition-colors ${
                                  person.subType === value
                                    ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                                    : 'border-gray-300 text-gray-600 hover:border-gray-400'
                                }`}
                              >
                                {label}
                              </button>
                            ))}
                          </div>
                          <input
                            type="text"
                            className="w-full border border-gray-300 rounded px-2 py-1 text-xs"
                            placeholder="ID Number"
                            value={person.idNo}
                            onChange={(e) => setScPwdPersons(prev => prev.map((p, i) =>
                              i === idx ? { ...p, idNo: e.target.value } : p
                            ))}
                          />
                          <input
                            type="text"
                            className="w-full border border-gray-300 rounded px-2 py-1 text-xs"
                            placeholder="Full Name"
                            value={person.name}
                            onChange={(e) => setScPwdPersons(prev => prev.map((p, i) =>
                              i === idx ? { ...p, name: e.target.value } : p
                            ))}
                          />
                        </div>
                      ))}
                    </div>

                    <p className="text-[10px] text-gray-400">Tick items on the left to apply discount.</p>
                  </div>
                )}

                {/* Manager discount amount field */}
                {discountType === 'MANAGER_DISCOUNT' && managerApproved && (
                  <div className="mt-2 space-y-2">
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1">Discount Amount (₱)</label>
                      <input type="number" min="0" step="0.01"
                        className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm"
                        value={managerDiscountAmount}
                        onChange={(e) => setManagerDiscountAmount(e.target.value)}
                        placeholder="0.00"
                        autoFocus
                      />
                    </div>
                    <p className="text-[10px] text-gray-400">Tick items on the left to limit discount scope.</p>
                  </div>
                )}

                {/* Employee discount — just item selection */}
                {discountType === 'EMPLOYEE_DISCOUNT' && managerApproved && (
                  <p className="mt-2 text-[10px] text-gray-400">
                    {employeeDiscountPct}% off selected items. Tick items on the left.
                  </p>
                )}
              </div>

              {/* Amount tendered (cash only) */}
              {transactionType === 'cash' && (
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Amount Tendered (₱)</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                    value={amountTendered}
                    onChange={(e) => setAmountTendered(e.target.value)}
                    placeholder="0.00"
                  />
                  {tenderedCents >= finalAmount && tenderedCents > 0 && (
                    <p className="text-xs text-green-700 mt-1">Change: {fmt(change)}</p>
                  )}
                </div>
              )}

              {/* Remarks */}
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Remarks</label>
                <textarea
                  rows={2}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-none"
                  value={remarks}
                  onChange={(e) => setRemarks(e.target.value)}
                  placeholder="Optional"
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
